import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, Music, FileText, Loader } from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';
import BadgeComponent from '../ui/BadgeComponent';
import ButtonComponent from '../ui/ButtonComponent';

interface WizardStep4Props {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

const AD_SUITABILITY_CATEGORIES = [
  { id: 'profanity', label: 'Profanity & Language' },
  { id: 'violence', label: 'Violence' },
  { id: 'adultContent', label: 'Adult Content' },
  { id: 'shockingContent', label: 'Shocking Content' },
  { id: 'harmfulActs', label: 'Harmful or Dangerous Acts' },
  { id: 'drugsAlcohol', label: 'Drugs & Alcohol' },
  { id: 'firearms', label: 'Firearms & Weapons' },
  { id: 'sensitivePolitics', label: 'Sensitive Politics' },
  { id: 'controversialIssues', label: 'Controversial Issues' },
];

const SEVERITY_LEVELS = ['none', 'mild', 'strong'];

export default function WizardStep4({ data, updateData }: WizardStep4Props) {
  const [checksRunning, setChecksRunning] = useState(false);
  const [checksComplete, setChecksComplete] = useState(false);
  const [checkResults, setCheckResults] = useState({
    copyright: 'pending',
    audioClaim: 'pending',
    brandSafety: 'pending',
    policyConflicts: 'pending',
    platformRestrictions: 'pending',
  });

  useEffect(() => {
    if (!data.complianceChecksComplete) {
      runComplianceChecks();
    }
  }, []);

  const runComplianceChecks = () => {
    setChecksRunning(true);
    setChecksComplete(false);

    // Simulate running checks
    const checks = ['copyright', 'audioClaim', 'brandSafety', 'policyConflicts', 'platformRestrictions'];
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (currentIndex < checks.length) {
        setCheckResults((prev) => ({
          ...prev,
          [checks[currentIndex]]: 'pass',
        }));
        currentIndex++;
      } else {
        clearInterval(interval);
        setChecksRunning(false);
        setChecksComplete(true);
        updateData({ complianceChecksComplete: true });
      }
    }, 800);
  };

  const handleAdSuitabilityChange = (category: string, value: string) => {
    updateData({
      adSuitability: {
        ...data.adSuitability,
        [category]: value,
      },
    });
  };

  const getCheckIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-yellow-600" />;
      case 'fail':
        return <AlertTriangle size={16} className="text-red-600" />;
      default:
        return <Loader size={16} className="text-[#9CA3AF] animate-spin" />;
    }
  };

  const getCheckStatus = (status: string) => {
    switch (status) {
      case 'pass':
        return { text: 'Passed', color: 'text-green-600' };
      case 'warning':
        return { text: 'Limited Visibility', color: 'text-yellow-600' };
      case 'fail':
        return { text: 'Cannot Publish', color: 'text-red-600' };
      default:
        return { text: 'Checking...', color: 'text-[#9CA3AF]' };
    }
  };

  return (
    <div className="space-y-8">
      {/* Age Restriction */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-medium text-[#111827]">Audience & Age Restriction</h3>
          <p className="text-xs text-[#6B7280] mt-1">
            Select the appropriate audience for your content
          </p>
        </div>
        <div className="p-6 space-y-3">
          <label className="flex items-start gap-3 p-4 border-2 border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
            <input
              type="radio"
              name="ageRestriction"
              checked={data.ageRestriction === 'all'}
              onChange={() => updateData({ ageRestriction: 'all' })}
              className="mt-0.5 w-4 h-4 text-[#2563EB] border-[#D1D5DB] focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
            />
            <div>
              <div className="text-sm font-medium text-[#111827]">Suitable for all audiences</div>
              <p className="text-xs text-[#6B7280] mt-1">No age restrictions</p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
            <input
              type="radio"
              name="ageRestriction"
              checked={data.ageRestriction === 'notForKids'}
              onChange={() => updateData({ ageRestriction: 'notForKids' })}
              className="mt-0.5 w-4 h-4 text-[#2563EB] border-[#D1D5DB] focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
            />
            <div>
              <div className="text-sm font-medium text-[#111827]">Not for kids</div>
              <p className="text-xs text-[#6B7280] mt-1">Content designed for teens and adults</p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
            <input
              type="radio"
              name="ageRestriction"
              checked={data.ageRestriction === '18+'}
              onChange={() => updateData({ ageRestriction: '18+' })}
              className="mt-0.5 w-4 h-4 text-[#2563EB] border-[#D1D5DB] focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
            />
            <div>
              <div className="text-sm font-medium text-[#111827]">18+ restricted</div>
              <p className="text-xs text-[#6B7280] mt-1">Contains mature content for adults only</p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
            <input
              type="radio"
              name="ageRestriction"
              checked={data.ageRestriction === 'mature'}
              onChange={() => updateData({ ageRestriction: 'mature' })}
              className="mt-0.5 w-4 h-4 text-[#2563EB] border-[#D1D5DB] focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
            />
            <div>
              <div className="text-sm font-medium text-[#111827]">Mature themes</div>
              <p className="text-xs text-[#6B7280] mt-1">Sensitive topics requiring discretion</p>
            </div>
          </label>
        </div>
      </CardComponent>

      {/* Ad Suitability Classification */}
      {data.monetizationEnabled && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={18} className="text-[#6B7280]" />
              <h3 className="text-sm font-medium text-[#111827]">Ad Suitability Classification</h3>
            </div>
            <p className="text-xs text-[#6B7280]">
              Required for monetized content. Select severity level for each category.
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {AD_SUITABILITY_CATEGORIES.map((category) => (
                <div key={category.id}>
                  <label className="block text-sm font-medium text-[#111827] mb-2">
                    {category.label}
                  </label>
                  <select
                    value={data.adSuitability[category.id as keyof typeof data.adSuitability]}
                    onChange={(e) => handleAdSuitabilityChange(category.id, e.target.value)}
                    className="w-full h-10 px-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
                  >
                    {SEVERITY_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </CardComponent>
      )}

      {/* Audio License */}
      {data.mediaType === 'video' && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <Music size={18} className="text-[#6B7280]" />
              <h3 className="text-sm font-medium text-[#111827]">Audio License</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg mb-4">
              <div>
                <div className="text-sm font-medium text-[#111827]">Detected: "Summer Vibes"</div>
                <div className="text-xs text-[#6B7280] mt-1">Artist: Audio Library</div>
                <div className="text-xs text-[#9CA3AF] mt-1">Detected automatically</div>
              </div>
              <BadgeComponent variant="success">✓ Licensed</BadgeComponent>
            </div>
            <ButtonComponent variant="secondary" size="sm">
              Verify License
            </ButtonComponent>
          </div>
        </CardComponent>
      )}

      {/* Subtitles & Accessibility */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[#6B7280]" />
            <h3 className="text-sm font-medium text-[#111827]">Subtitles & Accessibility</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-[#111827]">Auto-generate subtitles</span>
              <p className="text-xs text-[#6B7280] mt-1">Automatically create captions for your video</p>
            </div>
            <label className="relative inline-block w-12 h-6">
              <input
                type="checkbox"
                checked={data.subtitlesEnabled}
                onChange={(e) => updateData({ subtitlesEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <span className="absolute inset-0 bg-[#D1D5DB] rounded-full peer-checked:bg-[#10B981] transition-colors cursor-pointer"></span>
              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6 cursor-pointer shadow-sm"></span>
            </label>
          </label>

          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">
              ALT Text (Accessibility)
            </label>
            <textarea
              value={data.altText}
              onChange={(e) => updateData({ altText: e.target.value })}
              placeholder="Describe your content for screen readers..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] resize-none transition-all"
            />
          </div>
        </div>
      </CardComponent>

      {/* Legal & Platform Scan */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#111827]">Legal & Platform Compliance</h3>
              <p className="text-xs text-[#6B7280] mt-1">
                Running automated checks on your content
              </p>
            </div>
            {checksComplete && (
              <BadgeComponent variant="success">All Checks Passed</BadgeComponent>
            )}
          </div>
        </div>
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center gap-3">
              {getCheckIcon(checkResults.copyright)}
              <span className="text-sm text-[#111827]">Copyright scan</span>
            </div>
            <span className={`text-sm font-medium ${getCheckStatus(checkResults.copyright).color}`}>
              {getCheckStatus(checkResults.copyright).text}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center gap-3">
              {getCheckIcon(checkResults.audioClaim)}
              <span className="text-sm text-[#111827]">Audio claim check</span>
            </div>
            <span className={`text-sm font-medium ${getCheckStatus(checkResults.audioClaim).color}`}>
              {getCheckStatus(checkResults.audioClaim).text}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center gap-3">
              {getCheckIcon(checkResults.brandSafety)}
              <span className="text-sm text-[#111827]">Brand safety</span>
            </div>
            <span className={`text-sm font-medium ${getCheckStatus(checkResults.brandSafety).color}`}>
              {getCheckStatus(checkResults.brandSafety).text}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center gap-3">
              {getCheckIcon(checkResults.policyConflicts)}
              <span className="text-sm text-[#111827]">Policy conflicts</span>
            </div>
            <span className={`text-sm font-medium ${getCheckStatus(checkResults.policyConflicts).color}`}>
              {getCheckStatus(checkResults.policyConflicts).text}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center gap-3">
              {getCheckIcon(checkResults.platformRestrictions)}
              <span className="text-sm text-[#111827]">
                Platform restrictions ({data.platforms.join(', ')})
              </span>
            </div>
            <span className={`text-sm font-medium ${getCheckStatus(checkResults.platformRestrictions).color}`}>
              {getCheckStatus(checkResults.platformRestrictions).text}
            </span>
          </div>
        </div>
        {checksRunning && (
          <div className="px-6 pb-6">
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
              <Loader size={14} className="animate-spin" />
              <span>Running compliance checks...</span>
            </div>
          </div>
        )}
      </CardComponent>
    </div>
  );
}
